import React, { useEffect, useState } from "react";
import { Plus, Truck, Wallet, AlertTriangle, Package, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SupplierCard from "@/components/suppliers/SupplierCard";
import SupplierForm from "@/components/suppliers/SupplierForm";
import SupplierTxnForm from "@/components/suppliers/SupplierTxnForm";
import InvoiceDialog from "@/components/InvoiceDialog";
import InvoiceDetailDialog from "@/components/InvoiceDetailDialog";
import SupplierLedgerTable from "@/components/suppliers/SupplierLedgerTable";
import SupplierProducts from "@/components/suppliers/SupplierProducts";
import DailyPurchaseForm from "@/components/suppliers/DailyPurchaseForm";
import DailyPurchaseList from "@/components/suppliers/DailyPurchaseList";
import MonthlyPurchaseReport from "@/components/suppliers/MonthlyPurchaseReport";
import SearchBox, { matches } from "@/components/SearchBox";
import { takeJump } from "@/lib/jump";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(() => takeJump("suppliers")?.id || "ALL");
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [txnOpen, setTxnOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [detail, setDetail] = useState(null);
  const [txnType, setTxnType] = useState("purchase");
  const [productsOpen, setProductsOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [dailyEdit, setDailyEdit] = useState(null);
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().slice(0, 10));
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");
  const shownItems = items.filter((s) => matches(search, s.name, s.contact, s.phone, s.category));
  const shownTxns = txns.filter((t) => matches(search, t.supplier_name, t.description, t.invoice_no, t.amount));

  const loadAll = async () => {
    const [s, t, sm] = await Promise.all([
      api.get("/suppliers"),
      api.get("/supplier-ledger", { params: filter !== "ALL" ? { supplier_id: filter } : {} }),
      api.get("/suppliers/summary"),
    ]);
    setItems(s.data); setTxns(t.data); setSummary(sm.data); setRefreshKey((k) => k + 1);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filter]);

  const remove = async (id) => {
    try { await api.delete(`/suppliers/${id}`); toast.success("Tedarikçi silindi"); if (filter === id) setFilter("ALL"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openTxn = (type) => { setTxnType(type); setTxnOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Tedarikçi Cari Hesapları</h1>
          <p className="text-sm text-muted-foreground mt-1">Alış faturaları, ödemeler ve tedarikçilere kalan borç. Faturalar maliyete otomatik işlenir.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="supplier-add-btn" className="rounded-full h-11 px-5 gap-2"><Truck className="h-4 w-4" /> Yeni Tedarikçi</Button>
          <Button variant="outline" onClick={() => setProductsOpen(true)} data-testid="supplier-products-btn" className="rounded-full h-11 px-5 gap-2"><Package className="h-4 w-4" /> Ürünler & Fiyatlar</Button>
          <Button variant="outline" onClick={() => { setDailyEdit(null); setDailyOpen(true); }} data-testid="supplier-daily-purchase-btn" className="rounded-full h-11 px-5 gap-2 text-primary border-primary/40"><ShoppingBasket className="h-4 w-4" /> Günlük Alım</Button>
          <Button variant="outline" onClick={() => setInvoiceOpen(true)} data-testid="supplier-add-purchase-btn" className="rounded-full h-11 px-5 gap-2 text-rose-400 border-rose-400/40"><Plus className="h-4 w-4" /> Alış Faturası</Button>
          <Button onClick={() => openTxn("payment")} data-testid="supplier-add-payment-btn" className="rounded-full h-11 px-6 font-semibold gap-2"><Wallet className="h-4 w-4" /> Ödeme Yap</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Mini label="Tedarikçilere Borç" value={TRY(summary?.payable)} color="text-amber-400" testId="supplier-kpi-payable" />
        <Mini label="Toplam Alış" value={TRY(summary?.total_purchases)} color="text-rose-400" testId="supplier-kpi-purchases" />
        <Mini label="Toplam Ödenen" value={TRY(summary?.total_paid)} color="text-emerald-400" testId="supplier-kpi-paid" />
        <Mini label="Vadesi Geçen" value={TRY(summary?.overdue)} color="text-rose-400" icon={AlertTriangle} testId="supplier-kpi-overdue" />
      </div>

      <SearchBox value={search} onChange={setSearch} placeholder="Tedarikçi adı, açıklama, fatura no ara…" testId="supplier-search-input" className="w-full sm:w-[320px]" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {items.length === 0 && <Card className="p-8 text-center text-muted-foreground col-span-full" data-testid="supplier-empty">Henüz tedarikçi eklenmedi</Card>}
        {items.length > 0 && shownItems.length === 0 && <Card className="p-6 text-center text-muted-foreground col-span-full text-sm" data-testid="supplier-search-empty">Aramayla eşleşen tedarikçi yok</Card>}
        {shownItems.map((s) => (
          <SupplierCard key={s.id} s={s} selected={filter === s.id} onSelect={() => setFilter(filter === s.id ? "ALL" : s.id)}
            onEdit={() => { setEditItem(s); setFormOpen(true); }} onDelete={() => remove(s.id)} />
        ))}
      </div>

      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="h-11 w-full sm:w-[280px]" data-testid="supplier-ledger-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">Tüm Tedarikçiler</SelectItem>
          {items.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <DailyPurchaseList date={dailyDate} setDate={setDailyDate} supplierId={filter !== "ALL" ? filter : ""} refreshKey={refreshKey}
        onEdit={(r, deleted) => { if (deleted) { loadAll(); return; } setDailyEdit(r); setDailyOpen(true); }} />

      <MonthlyPurchaseReport refreshKey={refreshKey} />

      <SupplierLedgerTable txns={shownTxns} onChanged={loadAll} onEdit={(t) => { setEditInvoice(t); setInvoiceOpen(true); }} onDetail={setDetail} />
      <InvoiceDetailDialog invoice={detail} partyName={detail?.supplier_name} onClose={() => setDetail(null)} onEdit={(t) => { setEditInvoice(t); setInvoiceOpen(true); }} />
      <SupplierForm open={formOpen} onClose={() => setFormOpen(false)} item={editItem} onSaved={loadAll} />
      <SupplierTxnForm open={txnOpen} onClose={() => setTxnOpen(false)} type={txnType} suppliers={items}
        defaultId={filter !== "ALL" ? filter : ""} onSaved={loadAll} />
      <InvoiceDialog open={invoiceOpen} onClose={() => { setInvoiceOpen(false); setEditInvoice(null); }} kind="purchase" parties={items} defaultPartyId={filter !== "ALL" ? filter : ""} editItem={editInvoice} onSaved={loadAll} />
      <SupplierProducts open={productsOpen} onClose={() => setProductsOpen(false)} suppliers={items} defaultId={filter !== "ALL" ? filter : ""} onChanged={() => setRefreshKey((k) => k + 1)} />
      <DailyPurchaseForm open={dailyOpen} onClose={() => setDailyOpen(false)} suppliers={items} defaultId={filter !== "ALL" ? filter : ""}
        editItem={dailyEdit} onSaved={loadAll} onManageProducts={() => { setDailyOpen(false); setProductsOpen(true); }} />
    </div>
  );
}

const Mini = ({ label, value, color, icon: Icon, testId }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</div>
    <div className={`mt-2 font-mono text-lg sm:text-xl font-bold ${color}`}>{value}</div>
  </Card>
);
